import {
  EActionInputType,
  EDatasourceType,
  EFormSource,
  IActionDefinitionDto,
  IDatasourceDto,
  IProcessStepScreensInfo,
  IWorkFlow,
  IWorkFlowDto
} from "@kuika/kuika-cl-model";
import classNames from "classnames";
import _ from "lodash";
import React, { Component } from "react";
import reactFastCompare from "react-fast-compare";
import ActionService from "../../../services/action-service";
import { DatasourceService } from "../../../services/datasource-service";
import { IWithSDProps, withSD } from "../../../shared/hoc/screen-design-consumer";
import { KMainFunctions } from "../../../shared/utility/main-functions";
import ExcelSvg from "../../../svgs/excel.svg";
import ArrowDown from "../../../svgs/small-icons/arrow-down.svg";
import InfoSvg from "../../../svgs/small-icons/info.svg";
import { SchemaNode } from "../../design-tools/left-panel/datasource-panel/datasources";
import { KModalFooter } from "../../ui-kit/k-modal/k-modal-footer";
import KuikaButton from "../../ui-kit/kuika-button/kuika-button";
import KuikaCheckbox from "../../ui-kit/kuika-checkbox/kuika-checkbox";
import KuikaInput from "../../ui-kit/kuika-input/kuika-input";
import KuikaSelectbox, { KuikaSelectboxOption } from "../../ui-kit/kuika-selectbox/kuika-selectbox";
import KuikaTooltip from "../../ui-kit/kuika-tooltip/kuika-tooltip";

interface IFormWizardProps {
  isExcelUploadInProgress: boolean;
  isCreatingFormFromDataTableInProgress: boolean;
  isCreatingFormFromActionInProgress: boolean;
  isCreatingFormFromWfInProgress: boolean;
  onClickNextCustom: (alsoCreateTable: boolean, nameOfTheTableToBeCreated: string) => void;
  onClickNextDataTable: (selectedDataSourceId: string, selectedDataTableName: string) => void;
  onClickNextExcel: (excel: File) => void;
  onClickNextWf: (processId: string, stepId: string) => void;
  onClickNextAI: () => void;
  onClickNextAction: (
    actionDefinitionId: string,
    actionInputType: EActionInputType | undefined,
    selectedProcess: string | undefined
  ) => void;
  setFooter: (footer: React.ReactNode) => void;
  /** Closes the wizard — the footer's way out, beside the commit. */
  onCancel: () => void;
}

interface IFormWizardState {
  isAlsoCreateTableChecked: boolean;
  nameOfTheTableToBeCreated: string;
  isSchemaLoading: boolean;
  selectedSource: EFormSource | null;
  selectedDataSourceId: string | undefined;
  selectedDataTableName: string | undefined;
  selectedProcess: string | undefined;
  selectedActionId: string | undefined;
  selectedActionInputType: EActionInputType | undefined;
  schema: SchemaNode[];
  excel: File | null;
  /** Drag is over the workbook drop zone. */
  isExcelDragOver: boolean;
}

class FormWizardLanding extends Component<IFormWizardProps & IWithSDProps, IFormWizardState> {
  static initialState: IFormWizardState = {
    isAlsoCreateTableChecked: false,
    nameOfTheTableToBeCreated: "",
    isSchemaLoading: false,
    selectedSource: null,
    selectedActionId: undefined,
    selectedActionInputType: undefined,
    selectedDataSourceId: undefined,
    selectedDataTableName: undefined,
    selectedProcess: undefined,
    schema: [],
    excel: null,
    isExcelDragOver: false
  };

  constructor(props: IFormWizardProps & IWithSDProps) {
    super(props);
    this.state = { ...FormWizardLanding.initialState };
  }

  componentDidMount() {
    this.setFooter();
  }

  componentDidUpdate(prevProps: Readonly<IFormWizardProps & IWithSDProps>, prevState: Readonly<IFormWizardState>) {
    if (!reactFastCompare(prevState, this.state)) {
      this.setFooter();
    }
  }

  private setFooter = () => {
    this.props.setFooter(
      <KModalFooter
        submitLabel="Next"
        submitTestId="createFormNextButton"
        submitOnboardingId="common_form-wizard_next"
        onSubmit={this.onClickNext}
        onCancel={this.props.onCancel}
        disabled={this.getIsNextButtonDisabled()}
        loading={this.getIsNextButtonLoading()}
      />
    );
  };

  private getIsNextButtonLoading = (): boolean => {
    if (this.state.selectedSource === EFormSource.Excel && this.props.isExcelUploadInProgress) {
      return true;
    }

    if (this.state.selectedSource === EFormSource.DataTable && this.props.isCreatingFormFromDataTableInProgress) {
      return true;
    }

    if (this.state.selectedSource === EFormSource.Action && this.props.isCreatingFormFromActionInProgress) {
      return true;
    }

    if (this.state.selectedSource === EFormSource.WorkflowBo && this.props.isCreatingFormFromWfInProgress) {
      return true;
    }

    return false;
  };

  private getIsNextButtonDisabled = (): boolean => {
    if (this.state.selectedSource === null || this.getIsNextButtonLoading()) return true;

    if (
      this.state.selectedSource === EFormSource.DataTable &&
      (_.isEmpty(this.state.selectedDataSourceId) || _.isEmpty(this.state.selectedDataTableName))
    ) {
      return true;
    }

    if (
      this.state.selectedSource === EFormSource.Custom &&
      this.state.isAlsoCreateTableChecked &&
      (_.isEmpty(this.state.nameOfTheTableToBeCreated) || this.state.nameOfTheTableToBeCreated.length < 3)
    ) {
      return true;
    }

    // Not `_.isEmpty`: a File has no own enumerable keys, so lodash reads every chosen workbook as empty.
    if (this.state.selectedSource === EFormSource.Excel && !this.state.excel) {
      return true;
    }

    if (this.state.selectedSource === EFormSource.Action) {
      const actionDefinition = this.props.sd.actionDefinitions.find((x) => x.id === this.state.selectedActionId);

      if (!actionDefinition) return true;

      if (actionDefinition.name === "StartProcess") {
        if (_.isEmpty(this.state.selectedProcess)) return true;
      } else {
        if (_.isEmpty(this.state.selectedActionId)) return true;
        if (this.state.selectedActionInputType === undefined) return true;
      }
    }

    return false;
  };

  private static getOptionClassName = (isSelected: boolean): string => {
    return classNames("form-wizard-option-button", {
      "form-wizard-option-button--selected": isSelected
    });
  };

  private static getWithDataTableIcon = () => {
    return (
      <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none">
        <path d="M5.74991 3C4.24002 3 2.99991 4.24011 2.99991 5.75V14.627C2.98668 14.7075 2.98668 14.7896 2.99991 14.8701V18.25C2.99991 19.7599 4.24002 21 5.74991 21H18.2499C19.7598 21 20.9999 19.7599 20.9999 18.25V14.873C21.0131 14.7925 21.0131 14.7104 20.9999 14.6299V5.75C20.9999 4.24011 19.7598 3 18.2499 3H5.74991ZM5.74991 4.5H8.49991V8.5H4.49991V5.75C4.49991 5.05089 5.0508 4.5 5.74991 4.5ZM9.99991 4.5H18.2499C18.949 4.5 19.4999 5.05089 19.4999 5.75V8.5H9.99991V4.5ZM4.49991 10H8.49991V14H4.49991V10ZM9.99991 10H19.4999V14H9.99991V10ZM4.49991 15.5H8.49991V19.5H5.74991C5.0508 19.5 4.49991 18.9491 4.49991 18.25V15.5ZM9.99991 15.5H19.4999V18.25C19.4999 18.9491 18.949 19.5 18.2499 19.5H9.99991V15.5Z" />
      </svg>
    );
  };

  private static getWithWfIcon = () => {
    return (
      <svg
        className="form-wizard-wf-icon"
        width="32"
        height="32"
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M4.95522 15.2239H7.8209C8.34866 15.2239 8.77612 15.6513 8.77612 16.1791V19.0448C8.77612 19.5725 8.34866 20 7.8209 20H4.95522C4.42746 20 4 19.5725 4 19.0448V16.1791C4 15.6513 4.42746 15.2239 4.95522 15.2239Z"
          stroke="#818C99"
          strokeWidth="1.2"
          strokeMiterlimit="10"
          strokeLinecap="round"
          strokeLinejoin="round"
        ></path>
        <path
          d="M17.9921 4.25648L19.7435 6.00788C20.0855 6.34985 20.0855 6.90436 19.7435 7.24633L17.9921 8.99773C17.6502 9.3397 17.0961 9.3397 16.7541 8.99773L15.0027 7.24633C14.6608 6.90436 14.6608 6.34985 15.0027 6.00788L16.7541 4.25648C17.0961 3.91451 17.6502 3.91451 17.9921 4.25648Z"
          stroke="#818C99"
          strokeWidth="1.2"
          strokeMiterlimit="10"
          strokeLinecap="round"
          strokeLinejoin="round"
        ></path>
        <path
          d="M6.38806 9.01493C7.70695 9.01493 8.77612 7.94576 8.77612 6.62687C8.77612 5.30798 7.70695 4.23881 6.38806 4.23881C5.06917 4.23881 4 5.30798 4 6.62687C4 7.94576 5.06917 9.01493 6.38806 9.01493Z"
          stroke="#818C99"
          strokeWidth="1.2"
          strokeMiterlimit="10"
          strokeLinecap="round"
          strokeLinejoin="round"
        ></path>
        <path
          d="M17.3731 20C18.692 20 19.7612 18.9308 19.7612 17.6119C19.7612 16.2931 18.692 15.2239 17.3731 15.2239C16.0542 15.2239 14.9851 16.2931 14.9851 17.6119C14.9851 18.9308 16.0542 20 17.3731 20Z"
          stroke="#818C99"
          strokeWidth="1.2"
          strokeMiterlimit="10"
          strokeLinecap="round"
          strokeLinejoin="round"
        ></path>
        <path
          d="M6.38806 12.8358V11.403"
          stroke="#818C99"
          strokeWidth="1.2"
          strokeMiterlimit="10"
          strokeLinecap="round"
          strokeLinejoin="round"
        ></path>
        <path
          d="M11.1642 6.62687H12.597"
          stroke="#818C99"
          strokeWidth="1.2"
          strokeMiterlimit="10"
          strokeLinecap="round"
          strokeLinejoin="round"
        ></path>
        <path
          d="M17.3731 12.8358V11.403"
          stroke="#818C99"
          strokeWidth="1.2"
          strokeMiterlimit="10"
          strokeLinecap="round"
          strokeLinejoin="round"
        ></path>
      </svg>
    );
  };

  private static getWithExcelIcon = () => {
    return (
      <svg
        className="form-wizard-excel-icon"
        width="32"
        height="32"
        viewBox="0 0 64 64"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M35.333 12.6665H49.9997C52.9441 12.6665 55.333 15.0554 55.333 17.9998V47.3332C55.333 50.2776 52.9441 52.6665 49.9997 52.6665H35.333"
          stroke="#a3b0cc"
          strokeWidth="2"
          strokeMiterlimit="10"
          strokeLinejoin="round"
        />
        <path
          d="M35.3332 53.9445C35.3332 55.632 33.7915 56.8889 32.1457 56.5625L11.8818 52.507C10.0137 52.1389 8.6665 50.4931 8.6665 48.5903V15.4098C8.6665 13.507 10.0137 11.8612 11.8818 11.4862L32.1457 7.43754C33.7915 7.11115 35.3332 8.3681 35.3332 10.0556V53.9445Z"
          stroke="#a3b0cc"
          strokeWidth="2"
          strokeMiterlimit="10"
          strokeLinejoin="round"
        />
        <path
          d="M42 22H47.3333"
          stroke="#a3b0cc"
          strokeWidth="2"
          strokeMiterlimit="10"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M42 32.6665H47.3333"
          stroke="#a3b0cc"
          strokeWidth="2"
          strokeMiterlimit="10"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M42 43.3335H47.3333"
          stroke="#a3b0cc"
          strokeWidth="2"
          strokeMiterlimit="10"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M26.6108 41.5419C25.9511 41.5419 25.3052 41.2155 24.9233 40.6183L15.5899 25.9517C14.9997 25.0211 15.2705 23.785 16.2011 23.1878C17.1316 22.5975 18.3747 22.8683 18.9649 23.8058L28.2983 38.4725C28.8886 39.403 28.6177 40.6392 27.6872 41.2294C27.3538 41.4447 26.9788 41.5419 26.6108 41.5419Z"
          fill="#a3b0cc"
        />
        <path
          d="M17.2774 41.5419C16.9094 41.5419 16.5344 41.4447 16.2011 41.2294C15.2705 40.6392 14.9997 39.403 15.5899 38.4725L24.9233 23.8058C25.5205 22.8683 26.7566 22.5975 27.6872 23.1878C28.6177 23.785 28.8886 25.0211 28.2983 25.9517L18.9649 40.6183C18.583 41.2155 17.9372 41.5419 17.2774 41.5419Z"
          fill="#a3b0cc"
        />
      </svg>
    );
  };

  private static getWithActionIcon = () => {
    return (
      <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none">
        <path d="M8.25003 2C8.08713 2.00011 7.92869 2.05324 7.79866 2.15138C7.66864 2.24952 7.57409 2.38731 7.52933 2.54395L4.52932 13.0439C4.49746 13.1555 4.49192 13.273 4.51313 13.3871C4.53435 13.5012 4.58175 13.6088 4.65161 13.7014C4.72146 13.7941 4.81186 13.8693 4.9157 13.9211C5.01954 13.9729 5.13398 13.9999 5.25003 14H7.78909L6.02249 21.0684C5.98254 21.2287 5.99666 21.3978 6.06265 21.5493C6.12865 21.7008 6.24283 21.8262 6.38746 21.9062C6.53209 21.9861 6.69907 22.0161 6.86246 21.9913C7.02586 21.9666 7.17652 21.8887 7.29104 21.7695L19.291 9.26953C19.3929 9.16346 19.4613 9.02979 19.4878 8.88516C19.5142 8.74052 19.4976 8.59129 19.44 8.45602C19.3823 8.32074 19.2862 8.2054 19.1635 8.12432C19.0409 8.04325 18.8971 8.00001 18.75 8H14.291L15.9619 2.9873C15.9995 2.87452 16.0098 2.75442 15.9919 2.6369C15.9739 2.51938 15.9283 2.4078 15.8588 2.31137C15.7892 2.21495 15.6978 2.13643 15.5919 2.08229C15.4861 2.02815 15.3689 1.99995 15.25 2H8.25003ZM8.81546 3.5H14.209L12.5381 8.5127C12.5005 8.62548 12.4903 8.74558 12.5082 8.8631C12.5261 8.98062 12.5718 9.0922 12.6413 9.18863C12.7108 9.28505 12.8023 9.36357 12.9081 9.41771C13.014 9.47185 13.1311 9.50005 13.25 9.5H16.9903L8.16116 18.6973L9.47757 13.4316C9.50515 13.3211 9.50718 13.2057 9.48351 13.0943C9.45984 12.9828 9.4111 12.8782 9.34097 12.7884C9.27084 12.6986 9.18118 12.626 9.07879 12.576C8.9764 12.526 8.86397 12.5 8.75003 12.5H6.24417L8.81546 3.5Z" />
      </svg>
    );
  };

  private static getWithAIIcon = () => {
    return (
      <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none">
        <path d="M11.9883 1.98923C11.7895 1.99233 11.6002 2.07419 11.4617 2.21683C11.3233 2.35947 11.2471 2.55122 11.25 2.74997V3.49997H7.75C6.51625 3.49997 5.5 4.51622 5.5 5.74997V10.25C5.5 11.4837 6.51625 12.5 7.75 12.5H16.25C17.4838 12.5 18.5 11.4837 18.5 10.25V5.74997C18.5 4.51622 17.4838 3.49997 16.25 3.49997H12.75V2.74997C12.7514 2.64959 12.7327 2.54994 12.6949 2.45692C12.6571 2.36391 12.6011 2.27943 12.53 2.20849C12.459 2.13754 12.3745 2.08158 12.2814 2.04392C12.1883 2.00626 12.0887 1.98766 11.9883 1.98923ZM7.75 4.99997H16.25C16.6732 4.99997 17 5.32672 17 5.74997V10.25C17 10.6732 16.6732 11 16.25 11H7.75C7.32675 11 7 10.6732 7 10.25V5.74997C7 5.32672 7.32675 4.99997 7.75 4.99997ZM9.75 6.49997C9.41848 6.49997 9.10054 6.63167 8.86612 6.86609C8.6317 7.10051 8.5 7.41845 8.5 7.74997C8.5 8.08149 8.6317 8.39943 8.86612 8.63385C9.10054 8.86827 9.41848 8.99997 9.75 8.99997C10.0815 8.99997 10.3995 8.86827 10.6339 8.63385C10.8683 8.39943 11 8.08149 11 7.74997C11 7.41845 10.8683 7.10051 10.6339 6.86609C10.3995 6.63167 10.0815 6.49997 9.75 6.49997ZM14.25 6.49997C13.9185 6.49997 13.6005 6.63167 13.3661 6.86609C13.1317 7.10051 13 7.41845 13 7.74997C13 8.08149 13.1317 8.39943 13.3661 8.63385C13.6005 8.86827 13.9185 8.99997 14.25 8.99997C14.5815 8.99997 14.8995 8.86827 15.1339 8.63385C15.3683 8.39943 15.5 8.08149 15.5 7.74997C15.5 7.41845 15.3683 7.10051 15.1339 6.86609C14.8995 6.63167 14.5815 6.49997 14.25 6.49997ZM6.25 14C5.01625 14 4 15.0162 4 16.25V16.6318C4 17.8044 4.48109 18.9307 5.34375 19.7343C5.34375 19.7347 5.34375 19.735 5.34375 19.7353C6.50574 20.8172 8.60856 22 12 22C15.3914 22 17.4942 20.8169 18.6562 19.7343C19.5188 18.9309 20 17.8038 20 16.6308V16.25C20 15.0162 18.9838 14 17.75 14H6.25ZM6.25 15.5H17.75C18.1732 15.5 18.5 15.8267 18.5 16.25V16.6308C18.5 17.3949 18.1892 18.1202 17.6348 18.6367C16.7218 19.4871 15.0506 20.5 12 20.5C8.94944 20.5 7.27824 19.4868 6.36523 18.6367C5.81127 18.1203 5.5 17.395 5.5 16.6318V16.25C5.5 15.8267 5.82675 15.5 6.25 15.5Z" />
      </svg>
    );
  };

  private static getWithCustomIcon = () => {
    return (
      <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none">
        <path d="M16.2499 3C15.3958 3 14.6602 3.34732 14.1894 3.87695C13.8973 4.2056 13.7338 4.59556 13.6279 5H2.74994C2.65056 4.99859 2.55188 5.01696 2.45966 5.05402C2.36743 5.09108 2.28349 5.1461 2.21271 5.21588C2.14194 5.28566 2.08573 5.36882 2.04737 5.46051C2.00901 5.5522 1.98926 5.65061 1.98926 5.75C1.98926 5.84939 2.00901 5.9478 2.04737 6.03949C2.08573 6.13118 2.14194 6.21434 2.21271 6.28412C2.28349 6.3539 2.36743 6.40892 2.45966 6.44598C2.55188 6.48304 2.65056 6.50141 2.74994 6.5H13.6279C13.7338 6.90444 13.8973 7.2944 14.1894 7.62305C14.6602 8.15268 15.3958 8.5 16.2499 8.5C17.1041 8.5 17.8397 8.15268 18.3105 7.62305C18.6026 7.2944 18.766 6.90444 18.872 6.5H21.2499C21.3493 6.50141 21.448 6.48304 21.5402 6.44598C21.6324 6.40892 21.7164 6.3539 21.7872 6.28412C21.8579 6.21434 21.9141 6.13118 21.9525 6.03949C21.9909 5.9478 22.0106 5.84939 22.0106 5.75C22.0106 5.65061 21.9909 5.5522 21.9525 5.46051C21.9141 5.36882 21.8579 5.28566 21.7872 5.21588C21.7164 5.1461 21.6324 5.09108 21.5402 5.05402C21.448 5.01696 21.3493 4.99859 21.2499 5H18.872C18.766 4.59556 18.6026 4.2056 18.3105 3.87695C17.8397 3.34732 17.1041 3 16.2499 3ZM16.2499 4.5C16.7291 4.5 16.9935 4.65268 17.1894 4.87305C17.3853 5.09341 17.4999 5.41667 17.4999 5.75C17.4999 6.08333 17.3853 6.40659 17.1894 6.62695C16.9935 6.84732 16.7291 7 16.2499 7C15.7708 7 15.5064 6.84732 15.3105 6.62695C15.1146 6.40659 14.9999 6.08333 14.9999 5.75C14.9999 5.41667 15.1146 5.09341 15.3105 4.87305C15.5064 4.65268 15.7708 4.5 16.2499 4.5ZM7.74994 9.25C6.89577 9.25 6.16018 9.59732 5.68939 10.127C5.39726 10.4556 5.23384 10.8456 5.12787 11.25H2.74994C2.65056 11.2486 2.55188 11.267 2.45966 11.304C2.36743 11.3411 2.28349 11.3961 2.21271 11.4659C2.14194 11.5357 2.08573 11.6188 2.04737 11.7105C2.00901 11.8022 1.98926 11.9006 1.98926 12C1.98926 12.0994 2.00901 12.1978 2.04737 12.2895C2.08573 12.3812 2.14194 12.4643 2.21271 12.5341C2.28349 12.6039 2.36743 12.6589 2.45966 12.696C2.55188 12.733 2.65056 12.7514 2.74994 12.75H5.12787C5.23384 13.1544 5.39726 13.5444 5.68939 13.873C6.16018 14.4027 6.89577 14.75 7.74994 14.75C8.60411 14.75 9.3397 14.4027 9.81049 13.873C10.1026 13.5444 10.266 13.1544 10.372 12.75H21.2499C21.3493 12.7514 21.448 12.733 21.5402 12.696C21.6324 12.6589 21.7164 12.6039 21.7872 12.5341C21.8579 12.4643 21.9141 12.3812 21.9525 12.2895C21.9909 12.1978 22.0106 12.0994 22.0106 12C22.0106 11.9006 21.9909 11.8022 21.9525 11.7105C21.9141 11.6188 21.8579 11.5357 21.7872 11.4659C21.7164 11.3961 21.6324 11.3411 21.5402 11.304C21.448 11.267 21.3493 11.2486 21.2499 11.25H10.372C10.266 10.8456 10.1026 10.4556 9.81049 10.127C9.3397 9.59732 8.60411 9.25 7.74994 9.25ZM7.74994 10.75C8.22911 10.75 8.49351 10.9027 8.68939 11.123C8.88527 11.3434 8.99994 11.6667 8.99994 12C8.99994 12.3333 8.88527 12.6566 8.68939 12.877C8.49351 13.0973 8.22911 13.25 7.74994 13.25C7.27077 13.25 7.00637 13.0973 6.81049 12.877C6.61461 12.6566 6.49994 12.3333 6.49994 12C6.49994 11.6667 6.61461 11.3434 6.81049 11.123C7.00637 10.9027 7.27077 10.75 7.74994 10.75ZM15.2499 15.5C14.3958 15.5 13.6602 15.8473 13.1894 16.377C12.8973 16.7056 12.7338 17.0956 12.6279 17.5H2.74994C2.65056 17.4986 2.55188 17.517 2.45966 17.554C2.36743 17.5911 2.28349 17.6461 2.21271 17.7159C2.14194 17.7857 2.08573 17.8688 2.04737 17.9605C2.00901 18.0522 1.98926 18.1506 1.98926 18.25C1.98926 18.3494 2.00901 18.4478 2.04737 18.5395C2.08573 18.6312 2.14194 18.7143 2.21271 18.7841C2.28349 18.8539 2.36743 18.9089 2.45966 18.946C2.55188 18.983 2.65056 19.0014 2.74994 19H12.6279C12.7338 19.4044 12.8973 19.7944 13.1894 20.123C13.6602 20.6527 14.3958 21 15.2499 21C16.1041 21 16.8397 20.6527 17.3105 20.123C17.6026 19.7944 17.766 19.4044 17.872 19H21.2499C21.3493 19.0014 21.448 18.983 21.5402 18.946C21.6324 18.9089 21.7164 18.8539 21.7872 18.7841C21.8579 18.7143 21.9141 18.6312 21.9525 18.5395C21.9909 18.4478 22.0106 18.3494 22.0106 18.25C22.0106 18.1506 21.9909 18.0522 21.9525 17.9605C21.9141 17.8688 21.8579 17.7857 21.7872 17.7159C21.7164 17.6461 21.6324 17.5911 21.5402 17.554C21.448 17.517 21.3493 17.4986 21.2499 17.5H17.872C17.766 17.0956 17.6026 16.7056 17.3105 16.377C16.8397 15.8473 16.1041 15.5 15.2499 15.5ZM15.2499 17C15.7291 17 15.9935 17.1527 16.1894 17.373C16.3853 17.5934 16.4999 17.9167 16.4999 18.25C16.4999 18.5833 16.3853 18.9066 16.1894 19.127C15.9935 19.3473 15.7291 19.5 15.2499 19.5C14.7708 19.5 14.5064 19.3473 14.3105 19.127C14.1146 18.9066 13.9999 18.5833 13.9999 18.25C13.9999 17.9167 14.1146 17.5934 14.3105 17.373C14.5064 17.1527 14.7708 17 15.2499 17Z" />
      </svg>
    );
  };

  // Copied from screen-design-add.tsx
  private getSchema = async (dsId?: string) => {
    if (!dsId) {
      this.setState({ schema: [] });
      return;
    }

    const defaultKuikaTableNames = KMainFunctions.getDefaultKuikaTableNames();
    const allowedKuikaTables = ["KUser"];
    try {
      this.setState({ isSchemaLoading: true });
      const treeData: Array<SchemaNode> = [];
      const response = await DatasourceService.GetSchema(dsId);
      if (response.status === 200) {
        for (let index = 0; index < response.data.tables.length; index++) {
          const tbl = response.data.tables[index];
          if (!defaultKuikaTableNames.includes(tbl.name) || allowedKuikaTables.includes(tbl.name)) {
            treeData.push({
              title: `${tbl.schema}.${tbl.name}`,
              key: tbl.id,
              children: tbl.columns.map((x) => {
                return { title: x.name, key: x.id, isLeaf: true, children: [] };
              }),
              isLeaf: false
            });
          }
        }
      }
      this.setState({ schema: treeData });
    } catch {
    } finally {
      this.setState({ isSchemaLoading: false });
    }
  };

  private onSelectDataSource = (value: string | undefined) => {
    if (!value) {
      this.setState({
        selectedDataSourceId: undefined,
        selectedDataTableName: undefined
      });
      return;
    }

    if (this.state.selectedDataSourceId === value) return;

    this.setState({ selectedDataSourceId: value, selectedDataTableName: undefined }, () => {
      this.getSchema(value);
    });
  };

  private onSelectDataTable = (value: string | undefined) => {
    this.setState({ selectedDataTableName: value });
  };

  private onSelectAction = (value: string | undefined) => {
    if (!value) {
      this.setState({
        selectedActionId: undefined,
        selectedActionInputType: undefined,
        selectedProcess: undefined
      });
      return;
    }

    if (this.state.selectedActionId === value) {
      return;
    }

    this.setState({
      selectedActionId: value,
      selectedActionInputType: undefined,
      selectedProcess: undefined
    });
  };

  private renderNotFoundContent = () => {
    if (this.state.isSchemaLoading) {
      return <span>Loading...</span>;
    }

    return <span>No data found</span>;
  };

  /** One workbook, from the file picker or a drop; anything else is ignored. */
  private handleExcelUpload = (file: File | undefined | null) => {
    if (!file) return;

    this.setState({ excel: file });
  };

  private onChangeExcelInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    this.handleExcelUpload(e.target.files?.[0]);
    // Let the same workbook be chosen twice in a row.
    e.target.value = "";
  };

  private onDropExcel = (e: React.DragEvent<HTMLLabelElement>) => {
    e.preventDefault();
    this.setState({ isExcelDragOver: false });
    this.handleExcelUpload(e.dataTransfer?.files?.[0]);
  };

  private getActions = (): IActionDefinitionDto[] => {
    const actionDefinitions: IActionDefinitionDto[] = this.props.sd.actionDefinitions?.filter((actionDefinition) =>
      ActionService.getIsActionDefinitionUsableForDataOperations(actionDefinition)
    );

    actionDefinitions.forEach((actionDefinition) => {
      if (!actionDefinition.displayName) {
        actionDefinition.displayName = actionDefinition.name;
      }
    });

    return _.orderBy(actionDefinitions, "displayName", "asc");
  };

  private getName = (datasource: IDatasourceDto) => {
    if (datasource.datasourceType === EDatasourceType.ManagedDb) {
      return "Managed Db (SQL Server)";
    }
    if (datasource.datasourceType === EDatasourceType.ManagedDbOracle) {
      return "Managed Db (Oracle)";
    }

    if (datasource.datasourceType === EDatasourceType.ManagedDbPostgre) {
      return "Managed Db (Postgre)";
    }

    return datasource.name;
  };

  /** A datasource's option: its display name, under the id the E2E suite addresses it by. */
  private getDatasourceOptions = (): KuikaSelectboxOption[] => {
    const datasources = (this.props.sd?.datasources ?? []).filter(
      (ds) =>
        ds.datasourceType === EDatasourceType.ManagedDb ||
        ds.datasourceType === EDatasourceType.ManagedDbOracle ||
        ds.datasourceType === EDatasourceType.ManagedDbPostgre
    );

    return _.sortBy(datasources, (ds) => this.getName(ds).toLowerCase()).map((ds): KuikaSelectboxOption => {
      const testId: string = this.getName(ds).replace(/\s/g, "").replace("(", "").replace(")", "");

      return {
        value: ds.id,
        searchLabel: this.getName(ds),
        label: (
          <span data-testid={testId} data-onboarding-id={`common_form-wizard_ds-option-${testId}`}>
            {this.getName(ds)}
          </span>
        )
      };
    });
  };

  private getDataTableOptions = (): KuikaSelectboxOption[] => {
    return _.sortBy(this.state.schema ?? [], (table) => table.title.toLowerCase()).map(
      (table): KuikaSelectboxOption => {
        const testId: string = `${table.title.replace(/\s/g, "")}Table`;

        return {
          value: table.title,
          searchLabel: table.title,
          label: (
            <span
              data-testid={testId}
              data-onboarding-id={`common_form-wizard_table-option-${table.title.replace(/\s/g, "")}`}
            >
              {table.title}
            </span>
          )
        };
      }
    );
  };

  private renderDataTableSourceOptions = () => {
    return (
      <div className="form-wizard-source-options">
        <KuikaSelectbox
          data-testid="chooseDatasourceDropdown"
          data-onboarding-id="common_form-wizard_datasource-dropdown"
          searchable
          allowClear
          value={this.state.selectedDataSourceId ?? null}
          placeholder="Choose datasource..."
          options={this.getDatasourceOptions()}
          onChange={(value: string | number | null) =>
            this.onSelectDataSource(value === null ? undefined : String(value))
          }
          onClear={() => this.onSelectDataSource(undefined)}
        />
        <KuikaSelectbox
          data-testid="createFormModalChooseTableDropdown"
          data-onboarding-id="common_form-wizard_table-dropdown"
          searchable
          allowClear
          value={this.state.selectedDataTableName ?? null}
          placeholder="Choose table"
          options={this.getDataTableOptions()}
          onChange={(value: string | number | null) =>
            this.onSelectDataTable(value === null ? undefined : String(value))
          }
          onClear={() => this.onSelectDataTable(undefined)}
          dropdownFooter={this.state.schema?.length ? undefined : this.renderNotFoundContent()}
        />
      </div>
    );
  };

  private getActionOptions = (): KuikaSelectboxOption[] => {
    return this.getActions().map((actionDefinition: IActionDefinitionDto): KuikaSelectboxOption => {
      const label: string = actionDefinition.displayName ?? actionDefinition.name;

      return {
        value: actionDefinition.id,
        searchLabel: label,
        label: <span data-testid={`${actionDefinition.name.replace(/\s/g, "")}Action`}>{label}</span>
      };
    });
  };

  private renderActionSourceOptions = () => {
    const selectedActionsActionDefinition = this.getActions().find(
      (actionDefinition) => actionDefinition.id === this.state.selectedActionId
    );

    return (
      <div className="form-wizard-source-options">
        <div className="form-wizard-source-options__options_group">
          <KuikaSelectbox
            searchable
            allowClear
            value={this.state.selectedActionId ?? null}
            placeholder="Choose action"
            data-testid="createFormModalChooseActionDropdown"
            options={this.getActionOptions()}
            onChange={(value: string | number | null) =>
              this.onSelectAction(value === null ? undefined : String(value))
            }
            onClear={() => this.onSelectAction(undefined)}
            dropdownFooter={this.getActions().length ? undefined : this.renderNotFoundContent()}
          />
          {this.state.selectedActionId &&
            this.state.selectedActionId !== "" &&
            selectedActionsActionDefinition?.name !== "StartProcess" && (
              <div className="element-picker-with-action-modal_select-types__create_options">
                <button
                  className={classNames("element-picker-with-action-modal_select-types__render-type_option", {
                    "element-picker-with-action-modal_select-types__render-type_option__selected":
                      this.state.selectedActionInputType === EActionInputType.Parameters
                  })}
                  onClick={() =>
                    this.setState({
                      selectedActionInputType: EActionInputType.Parameters
                    })
                  }
                  disabled={this.getIsParametersDisabled()}
                >
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path
                      d="M4.75 4C3.24011 4 2 5.24011 2 6.75V17.25C2 18.7599 3.24011 20 4.75 20H19.25C20.7599 20 22 18.7599 22 17.25V6.75C22 5.24011 20.7599 4 19.25 4H4.75ZM4.75 5.5H19.25C19.9491 5.5 20.5 6.05089 20.5 6.75V17.25C20.5 17.9491 19.9491 18.5 19.25 18.5H4.75C4.05089 18.5 3.5 17.9491 3.5 17.25V6.75C3.5 6.05089 4.05089 5.5 4.75 5.5Z"
                      fill={this.getParametersIconColor()}
                    />
                    <path
                      d="M12.0966 12.9071L14.6793 10.2527C14.9591 9.96515 15.4133 9.96515 15.6932 10.2527C15.973 10.5403 15.973 11.0072 15.6932 11.2948L12.5745 14.5C12.3102 14.7716 11.8823 14.7716 11.6187 14.5L8.5 11.2948C8.22017 11.0072 8.22017 10.5403 8.5 10.2527C8.77983 9.96515 9.23405 9.96515 9.51388 10.2527L12.0966 12.9071Z"
                      fill={this.getParametersIconColor()}
                    />
                  </svg>
                  <div data-testid="createFormModalInputsButton">Inputs</div>
                </button>
                <button
                  className={classNames("element-picker-with-action-modal_select-types__render-type_option", {
                    "element-picker-with-action-modal_select-types__render-type_option__selected":
                      this.state.selectedActionInputType === EActionInputType.ReturnValue
                  })}
                  onClick={() =>
                    this.setState({
                      selectedActionInputType: EActionInputType.ReturnValue
                    })
                  }
                  disabled={this.getIsReturnValueDisabled()}
                >
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path
                      d="M4.75 4C3.24011 4 2 5.24011 2 6.75V17.25C2 18.7599 3.24011 20 4.75 20H19.25C20.7599 20 22 18.7599 22 17.25V6.75C22 5.24011 20.7599 4 19.25 4H4.75ZM4.75 5.5H19.25C19.9491 5.5 20.5 6.05089 20.5 6.75V17.25C20.5 17.9491 19.9491 18.5 19.25 18.5H4.75C4.05089 18.5 3.5 17.9491 3.5 17.25V6.75C3.5 6.05089 4.05089 5.5 4.75 5.5Z"
                      fill={this.getReturnValueIconColor()}
                    />
                    <path
                      d="M11.4792 8.22687C11.6151 8.08724 11.8 8.00599 11.9948 8.00031C12.1458 7.99596 12.2945 8.03729 12.4216 8.11889C12.5487 8.20049 12.6482 8.31856 12.7071 8.45764C12.766 8.59673 12.7815 8.75035 12.7516 8.8984C12.7218 9.04646 12.6479 9.18204 12.5397 9.28742L10.57 11.2571H16.2594C16.3588 11.2557 16.4575 11.2741 16.5497 11.3112C16.6419 11.3482 16.7259 11.4032 16.7967 11.473C16.8674 11.5428 16.9236 11.626 16.962 11.7177C17.0004 11.8094 17.0201 11.9078 17.0201 12.0071C17.0201 12.1065 17.0004 12.2049 16.962 12.2966C16.9236 12.3883 16.8674 12.4715 16.7967 12.5413C16.7259 12.611 16.6419 12.6661 16.5497 12.7031C16.4575 12.7402 16.3588 12.7586 16.2594 12.7571H10.57L12.5397 14.7269C12.6117 14.796 12.6691 14.8788 12.7087 14.9704C12.7483 15.062 12.7692 15.1605 12.7703 15.2603C12.7713 15.3601 12.7524 15.4591 12.7146 15.5514C12.6769 15.6438 12.6212 15.7278 12.5506 15.7983C12.48 15.8689 12.3961 15.9246 12.3037 15.9624C12.2113 16.0001 12.1124 16.019 12.0126 16.018C11.9128 16.017 11.8142 15.996 11.7226 15.9565C11.631 15.9169 11.5483 15.8594 11.4792 15.7874L8.29752 12.6058C8.20541 12.5361 8.13064 12.4461 8.07903 12.3427C8.02743 12.2394 8.00038 12.1256 8 12.0101C7.99963 11.8946 8.02593 11.7805 8.07685 11.6769C8.12778 11.5732 8.20196 11.4827 8.29361 11.4124L11.4792 8.22687Z"
                      fill={this.getReturnValueIconColor()}
                    />
                  </svg>
                  <div data-testid="createFormModalOutputsButton">Outputs</div>
                </button>
              </div>
            )}
          {selectedActionsActionDefinition?.name === "StartProcess" && (
            <div className="element-picker-with-action-modal_select-types__create_options">
              <KuikaSelectbox
                value={this.state.selectedProcess ?? null}
                placeholder="Choose process to start"
                searchable
                options={this.props.sd.workFlowList.map((process): KuikaSelectboxOption => ({
                  value: process.id,
                  label: process.name,
                  searchLabel: process.name
                }))}
                onChange={(value: string | number | null) =>
                  this.setState({ selectedProcess: value === null ? undefined : String(value) })
                }
                data-testid="createFormModalChooseProcessDropdown"
              />
            </div>
          )}
        </div>
      </div>
    );
  };

  private renderCustomSourceOptions = () => {
    return (
      <div className="form-wizard-source-options">
        <div className="form-wizard-source-options__options_group">
          <KuikaCheckbox
            checked={this.state.isAlsoCreateTableChecked}
            onChange={this.onChangeIsAlsoCreateTableChecked}
            dataTestId="createFormModalCreateTableInManagedDbCheckbox"
            label={
              <span className="form-wizard-source-options__checkbox-label">
                <span>Also create a table in ManagedDB</span>
                <KuikaTooltip content="If you choose this option, a table will be created in ManagedDB, and the form will be in sync with the table.">
                  <InfoSvg
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                    }}
                  />
                </KuikaTooltip>
              </span>
            }
          />
          {this.state.isAlsoCreateTableChecked && (
            <div className="form-wizard-source-options__table-name">
              <KuikaInput
                placeholder="Table Name"
                data-testid="createFormModalCustomOptionTableNameInput"
                value={this.state.nameOfTheTableToBeCreated}
                onChange={this.onChangeNameOfTheTableToBeCreated}
                onPressEnter={this.onClickNext}
              />
              {!_.isEmpty(this.state.nameOfTheTableToBeCreated) && this.state.nameOfTheTableToBeCreated.length < 3 && (
                <small>Hint: Table name should be at least 3 characters long.</small>
              )}
            </div>
          )}
        </div>
      </div>
    );
  };

  /**
   * The workbook drop zone. It is a `<label>` around a hidden file input rather than antd's
   * Dragger: the input keeps the `formWizardExcelDragger` id the E2E suite sets files on,
   * and clicking anywhere in the zone still opens the picker because the label owns it.
   */
  private renderExcelSourceOptions = () => {
    return (
      <label
        className={classNames("form-wizard-excel-dropzone", {
          "form-wizard-excel-dropzone--dragging": this.state.isExcelDragOver
        })}
        onDragOver={(e: React.DragEvent<HTMLLabelElement>) => {
          e.preventDefault();
          if (!this.state.isExcelDragOver) {
            this.setState({ isExcelDragOver: true });
          }
        }}
        onDragLeave={() => this.setState({ isExcelDragOver: false })}
        onDrop={this.onDropExcel}
      >
        <input
          id="formWizardExcelDragger"
          type="file"
          accept=".xlsx, .xls"
          className="form-wizard-excel-dropzone__input"
          onChange={this.onChangeExcelInput}
        />
        <div className="form-wizard-excel-drag-drop-area">
          <ExcelSvg />
          {!this.state.excel ? (
            <span>
              {`Drag & drop here or `}
              <span className="form-wizard-excel-dropzone__browse">Choose from computer</span>
            </span>
          ) : (
            <span>{this.state.excel.name}</span>
          )}
        </div>
      </label>
    );
  };

  private getSelectedSourceOptions = (source: EFormSource | number): React.ReactNode => {
    if (source === EFormSource.DataTable) {
      return this.renderDataTableSourceOptions();
    }

    if (source === EFormSource.Action) {
      return this.renderActionSourceOptions();
    }

    if (source === EFormSource.Custom) {
      return this.renderCustomSourceOptions();
    }

    if (source === EFormSource.Excel) {
      return this.renderExcelSourceOptions();
    }

    return <></>;
  };

  private onChangeNameOfTheTableToBeCreated = (e: React.ChangeEvent<HTMLInputElement>) => {
    this.setState({ nameOfTheTableToBeCreated: e.target.value });
  };

  private onChangeIsAlsoCreateTableChecked = (e: React.ChangeEvent<HTMLInputElement>) => {
    this.setState({
      isAlsoCreateTableChecked: e.target.checked,
      nameOfTheTableToBeCreated: ""
    });
  };

  private onClickNext = () => {
    if (this.getIsNextButtonDisabled() || this.state.selectedSource === null) return;

    if (
      this.state.selectedSource === EFormSource.DataTable &&
      this.state.selectedDataSourceId &&
      this.state.selectedDataTableName
    ) {
      this.props.onClickNextDataTable(this.state.selectedDataSourceId, this.state.selectedDataTableName);
    }

    if (this.state.selectedSource === EFormSource.Excel && this.state.excel) {
      this.props.onClickNextExcel(this.state.excel);
    }

    if (this.state.selectedSource === EFormSource.Action && this.state.selectedActionId) {
      this.props.onClickNextAction(
        this.state.selectedActionId,
        this.state.selectedActionInputType,
        this.state.selectedProcess
      );
    }

    if (this.state.selectedSource === EFormSource.AI) {
      this.props.onClickNextAI();
    }

    if (this.state.selectedSource === EFormSource.WorkflowBo) {
      const processStepScreen = this.props.sd.project.processStepScreenIds?.find(
        (p) => p.screenId === this.props.sd.sdId
      );

      if (!processStepScreen) return;
      this.props.onClickNextWf(processStepScreen.processId, processStepScreen.stepId);
    }

    if (this.state.selectedSource === EFormSource.Custom) {
      this.props.onClickNextCustom(this.state.isAlsoCreateTableChecked, this.state.nameOfTheTableToBeCreated);
    }
  };

  private resetSourceOptions = () => {
    this.setState({ ...FormWizardLanding.initialState });
  };

  private onClickExcel = () => {
    if (this.state.selectedSource === EFormSource.Excel) return;

    this.resetSourceOptions();
    this.setState({ selectedSource: EFormSource.Excel });
  };

  private onClickDataTable = () => {
    if (this.state.selectedSource === EFormSource.DataTable) return;

    this.resetSourceOptions();
    this.setState({ selectedSource: EFormSource.DataTable });
  };

  private onClickWorkflowBo = () => {
    if (this.state.selectedSource === EFormSource.WorkflowBo) return;

    this.resetSourceOptions();
    this.setState({ selectedSource: EFormSource.WorkflowBo });
  };

  private onClickAction = () => {
    if (this.state.selectedSource === EFormSource.Action) return;

    this.resetSourceOptions();
    this.setState({ selectedSource: EFormSource.Action });
  };

  private onClickAI = () => {
    if (this.state.selectedSource === EFormSource.AI) return;

    this.resetSourceOptions();
    this.setState({ selectedSource: EFormSource.AI });
  };

  private onClickCustom = () => {
    if (this.state.selectedSource === EFormSource.Custom) return;

    this.resetSourceOptions();
    this.setState({ selectedSource: EFormSource.Custom });
  };

  private getIsParametersDisabled(): boolean {
    if (!this.state.selectedActionId) return true;
    const actionDefinition = this.props.sd?.actionDefinitions?.find((x) => x.id === this.state.selectedActionId);

    if (!actionDefinition) return true;

    return !ActionService.getIsActionHaveInputs(actionDefinition);
  }

  private getIsReturnValueDisabled(): boolean {
    if (!this.state.selectedActionId) return true;
    const actionDefinition = this.props.sd?.actionDefinitions?.find((x) => x.id === this.state.selectedActionId);

    if (!actionDefinition) return true;

    return !ActionService.getIsActionHaveOutputs(actionDefinition);
  }

  private getParametersIconColor = (): string => {
    if (this.getIsParametersDisabled()) {
      return "#A3B0CC";
    }

    return this.state.selectedActionInputType === EActionInputType.Parameters ? "#ffffff" : "#7A8499";
  };

  private getReturnValueIconColor = (): string => {
    if (this.getIsReturnValueDisabled()) {
      return "#A3B0CC";
    }

    return this.state.selectedActionInputType === EActionInputType.ReturnValue ? "#ffffff" : "#7A8499";
  };

  private getProcessStepScreen(): IProcessStepScreensInfo | undefined {
    return this.props.sd.project.processStepScreenIds?.find((p) => p.screenId === this.props.sd.sdId);
  }

  private getWf = (processStepScreen: IProcessStepScreensInfo): IWorkFlow | undefined => {
    let wf: IWorkFlow | undefined;

    if (processStepScreen.projectId && processStepScreen.projectId !== "") {
      const projectWithWf = this.props.sd.projectsWithWFData.find(
        (project) => project.id === processStepScreen?.projectId
      );

      const workflows = projectWithWf?.workflows as IWorkFlowDto[] | undefined;

      wf = workflows?.find((wf) => wf.id === processStepScreen?.processId);
    } else {
      wf = this.props.sd.workFlowList.find((wf) => wf.id === processStepScreen?.processId);
    }

    return wf;
  };

  render() {
    const processStepScreen = this.getProcessStepScreen();
    const wf: IWorkFlow | undefined = processStepScreen ? this.getWf(processStepScreen) : undefined;

    return (
      <div className="form-wizard-inner-container">
        <div
          data-testid="createFormDescription"
          data-onboarding-id="common_form-wizard_description"
          className="form-wizard-description"
        >
          <span>Choose the source of your form</span>
        </div>
        <div className="form-wizard-option-buttons">
          <button
            data-testid="createFormDataTableOption"
            data-onboarding-id="common_form-wizard_datatable-option"
            className={FormWizardLanding.getOptionClassName(this.state.selectedSource === EFormSource.DataTable)}
            onClick={this.onClickDataTable}
          >
            {FormWizardLanding.getWithDataTableIcon()}
            <span>Data Table</span>
          </button>
          {wf && wf.parameters && wf.parameters.length > 0 && (
            <button
              className={FormWizardLanding.getOptionClassName(this.state.selectedSource === EFormSource.WorkflowBo)}
              onClick={this.onClickWorkflowBo}
            >
              {FormWizardLanding.getWithWfIcon()}
              <span>Process Automation</span>
            </button>
          )}
          <button
            data-testid="createFormExcelOption"
            data-onboarding-id="common_form-wizard_excel-option"
            className={FormWizardLanding.getOptionClassName(this.state.selectedSource === EFormSource.Excel)}
            onClick={this.onClickExcel}
          >
            {FormWizardLanding.getWithExcelIcon()}
            <span>Excel</span>
          </button>
          <button
            data-testid="createFormActionOption"
            data-onboarding-id="common_form-wizard_action-option"
            className={FormWizardLanding.getOptionClassName(this.state.selectedSource === EFormSource.Action)}
            onClick={this.onClickAction}
          >
            {FormWizardLanding.getWithActionIcon()}
            <span>Action</span>
          </button>
          {/* <button */}
          {/*  className={FormWizardLanding.getOptionClassName(this.state.selectedSource === EFormSource.AI)} */}
          {/*  onClick={this.onClickAI} */}
          {/* > */}
          {/*  {FormWizardLanding.getWithAIIcon()} */}
          {/*  <span>With AI</span> */}
          {/* </button> */}
          <button
            data-testid="createFormCustomOption"
            data-onboarding-id="common_form-wizard_custom-option"
            className={FormWizardLanding.getOptionClassName(this.state.selectedSource === EFormSource.Custom)}
            onClick={this.onClickCustom}
          >
            {FormWizardLanding.getWithCustomIcon()}
            <span>Custom</span>
          </button>
        </div>

        {this.state.selectedSource !== null && this.getSelectedSourceOptions(this.state.selectedSource)}
      </div>
    );
  }
}

const formWizardLanding = withSD<IFormWizardProps>(FormWizardLanding);
export { formWizardLanding as FormWizardLanding };
